import React from "react";
import { isAdminRole } from "../../app/access";
import { useSession } from "../../app/SessionContext.jsx";
import { listActiveByShelterId, verifyAddressStep } from "../../data/deliveryAddresses";
import { getRequestNotesById } from "../../data/requests";
import {
  completeTopUpApprovalTask,
  getTopUpApprovalTask,
  updateTaskByIdReturning,
} from "../../data/requestTasks";
import {
  confirmStockTopUp,
  getStockTopUpHeader,
  getTopUpRequestById,
  listStockTopUpLines,
} from "../../data/stockTopUp";
import { useCommentPanel } from "../../hooks/useCommentPanel";
import {
  BAG_COLOURS,
  BAG_LEVELS,
  HAT_COLOURS,
  HAT_SIZES,
  lineLabel,
  SECTION_LABELS,
  SECTION_ORDER,
  sectionUnitLabel,
} from "../../services/stockTopUpForm";
import { queueTopUpSalesOrders } from "../../services/topUpSalesOrderCreation";
import { logAudit } from "../../utils/auditLog";
import { GLYPH } from "../../utils/glyphs";
import { fmtDateLocal, fmtDateTimeLocal } from "../../utils/helpers";
import { Btn, Card } from "../../utils/ui.jsx";
import { CommentPanel } from "../request/CommentPanel.jsx";
import { EditAddressModal } from "../request/EditAddressModal.jsx";
import { TemplatePickerModal } from "../request/TemplatePickerModal.jsx";
import { TopUpTopInfoStrip } from "../request/TopUpTopInfoStrip.jsx";

// The request moves here on Confirm & Send (Phase 3b). Phase 4 also emits SOs + picker
// tasks at this same point.
const TERMINAL_STATUS = "Fully Processed";
const DECISIONS = ["pending", "approved", "declined"];
const DECISION_LABEL = { pending: "Pending", approved: "Approved", declined: "Declined" };
// Amber (Pending) / Green (Approved) / Red (Declined).
const DECISION_COLOUR = { pending: "#B7791F", approved: "#2E7D32", declined: "#C62828" };
// Sections that show a "Total Items" above the decision (bags/hats/socks have minimums).
const SECTIONS_WITH_TOTAL = ["bags", "hats", "socks"];

const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#607D79",
  fontWeight: 700,
  padding: "8px 10px",
  borderBottom: "1px solid #ECEFF1",
};
const itemHead = { ...th, minWidth: 220 };
const qtyHead = { ...th, textAlign: "center", padding: "8px 24px" };
const cell = { fontSize: 13, color: "#1A2E2B", padding: "8px 10px" };
const qtyCell = { ...cell, textAlign: "center", padding: "8px 24px" };
// The pack-unit chip (socks / bulk) shown beside the item — the ordering unit, display-only.
const unitChip = {
  display: "inline-block",
  marginLeft: 8,
  fontSize: 12,
  fontWeight: 600,
  color: "#00695C",
  background: "#FFF4D6",
  border: "1px solid #EAC44E",
  borderRadius: 5,
  padding: "1px 7px",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

// The tint a section's decision paints on its tab.
function decisionBg(dec, active) {
  if (active) return "var(--color-zephyr-teal)";
  if (dec === "approved") return "#E8F5E9";
  if (dec === "declined") return "#FFEBEE";
  return "#fff";
}

// Top-up approval view (stock-top-ups plan §4): one tab per section. The APPROVAL
// DECISION is ONE per section (Pending default / Approved / Declined) via the colour-
// coded dropdown at the bottom-right of the card; the approved quantity stays editable
// per line. One final Confirm & Send writes it all (ephemeral staging — nothing persists
// until Confirm, via confirm_stock_top_up_atomic).
export default function TopUpApprovalView({ requestId, onDone }) {
  const { user } = useSession();
  const [req, setReq] = React.useState(null);
  const [note, setNote] = React.useState("");
  const [lines, setLines] = React.useState(null);
  const [approvedQty, setApprovedQty] = React.useState({}); // line id -> string
  const [decisions, setDecisions] = React.useState({}); // section -> pending|approved|declined
  const [activeSection, setActiveSection] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [approvalTask, setApprovalTask] = React.useState(null);
  const [showAddressModal, setShowAddressModal] = React.useState(false);
  const [editDelivery, setEditDelivery] = React.useState("");
  const [saved, setSaved] = React.useState("");
  const [regAddrs, setRegAddrs] = React.useState([]); // string[] for the unregistered check
  const [regAddrObjs, setRegAddrObjs] = React.useState([]); // full rows for the Edit picker
  const [addrsLoaded, setAddrsLoaded] = React.useState(false);
  const isSupervisor = isAdminRole(user);

  // Comments (John, 5 Sep 2026 — missing when this header was first built). A top-up is
  // a `requests` row, so the same internal CommentPanel a responder uses on a request
  // works here; this screen just doesn't live under RequestDetail, so the state that
  // RequestDetail owns is set up locally. isZephyr = true (the Top-up Task List is
  // staff-only).
  const { showCommentPanel, setShowCommentPanel } = useCommentPanel();
  const [notes, setNotes] = React.useState([]);
  // The comment-compose textarea value (distinct from `note` above, which is the
  // top-up's own Other/Notes header text).
  const [commentText, setCommentText] = React.useState("");
  const [emailInvoicing, setEmailInvoicing] = React.useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = React.useState(false);

  React.useEffect(
    function () {
      let alive = true;
      getRequestNotesById(requestId).then(function (n) {
        if (alive) setNotes(Array.isArray(n) ? n : []);
      });
      return function () {
        alive = false;
      };
    },
    [requestId]
  );

  React.useEffect(
    function () {
      let alive = true;
      Promise.all([
        getTopUpRequestById(requestId),
        listStockTopUpLines(requestId),
        getStockTopUpHeader(requestId),
        getTopUpApprovalTask(requestId),
      ]).then(function ([r, ls, hdr, task]) {
        if (!alive) return;
        setReq(r);
        // Registered addresses for the delivery Edit picker + the unregistered warning
        // (self-loaded rather than via useShelterAddresses, whose effect keys on
        // requestId only — our shelter_id arrives later, with the async req load).
        if (r?.shelter_id) {
          listActiveByShelterId(r.shelter_id).then(function (rows) {
            if (!alive) return;
            const usable = (rows || []).filter(function (a) {
              return a.banned_address !== true;
            });
            setRegAddrObjs(usable);
            setRegAddrs(
              usable.map(function (a) {
                return [a.street, a.street2, a.suburb_city || a.suburb, a.state, a.postcode]
                  .filter(Boolean)
                  .join(", ");
              })
            );
            setAddrsLoaded(true);
          });
        } else {
          setAddrsLoaded(true);
        }
        setNote(hdr?.note || "");
        setApprovalTask(task);
        setLines(ls);
        const qty = {};
        for (const l of ls) {
          const q = l.approved_qty != null ? l.approved_qty : l.qty_required;
          qty[l.id] = q != null ? String(q) : "";
        }
        setApprovedQty(qty);
        const dec = {};
        for (const s of SECTION_ORDER) {
          const sl = ls.filter((l) => l.section === s);
          if (sl.length === 0) continue;
          const decided = sl.find((l) => l.approval_status && l.approval_status !== "pending");
          dec[s] = decided ? decided.approval_status : "pending";
        }
        setDecisions(dec);
        const present = SECTION_ORDER.filter((s) => ls.some((l) => l.section === s));
        setActiveSection(present[0] || null);
      });
      return function () {
        alive = false;
      };
    },
    [requestId]
  );

  function setQty(id, value) {
    setApprovedQty((prev) => ({ ...prev, [id]: value.replace(/[^0-9]/g, "") }));
  }
  function setDecision(section, d) {
    setDecisions((prev) => ({ ...prev, [section]: d }));
  }

  function flash(msg) {
    setSaved(msg);
    setTimeout(function () {
      setSaved("");
    }, 3000);
  }

  // Verify the top-up's delivery address (address-level; same leaf as the responder
  // view). Clears the amber "pending" warning in-memory immediately.
  async function verifyDeliveryAddress() {
    if (!req?.delivery_address_id) return;
    const res = await verifyAddressStep(req.delivery_address_id);
    if (!res) {
      flash("Verify failed");
      return;
    }
    logAudit("delivery_address_verified", {
      id: req.delivery_address_id,
      request_id: req.id,
      ref_no: req.ref_no,
      use_count: res.use_count,
      promoted: res.zephyr_verified,
      by: user?.email || "",
    });
    setReq(function (r) {
      return Object.assign({}, r, { delivery_address_verified: true });
    });
    flash(res.zephyr_verified ? "Address verified & added to the list" : "Address verified");
  }

  async function handleConfirm() {
    setErr("");
    const present = SECTION_ORDER.filter((s) => (lines || []).some((l) => l.section === s));
    const undecided = present.filter((s) => (decisions[s] || "pending") === "pending");
    if (undecided.length > 0) {
      setErr(
        `Please decide every section (Approved or Declined) before confirming — still pending: ${undecided
          .map((s) => SECTION_LABELS[s])
          .join(", ")}.`
      );
      return;
    }
    setSubmitting(true);
    const payload = (lines || []).map((l) => {
      const decision = decisions[l.section] || "pending";
      const declined = decision === "declined";
      const parsed = Number.parseInt(approvedQty[l.id] || "", 10);
      return {
        id: l.id,
        approved_qty: declined ? 0 : Number.isFinite(parsed) ? parsed : null,
        approval_status: decision,
      };
    });
    const res = await confirmStockTopUp(requestId, payload, TERMINAL_STATUS);
    if (res.error) {
      setErr(`Confirm failed: ${res.error.message}`);
      setSubmitting(false);
      return;
    }
    // Phase 4b — build the Sales Orders for the approved sections and park them on
    // `hold` for the Pickers side (best-effort; the approval is already committed, so
    // an SO-build hiccup must never surface as a confirm failure). Only APPROVED lines
    // with a positive quantity feed the builder; declined sections emit nothing.
    const approvedLines = (lines || [])
      .filter((l) => (decisions[l.section] || "pending") === "approved")
      .map((l) => {
        const parsed = Number.parseInt(approvedQty[l.id] || "", 10);
        return {
          section: l.section,
          item_key: l.item_key,
          item_name: l.item_name,
          colour: l.colour,
          size: l.size,
          year_level: l.year_level,
          level: l.level,
          amount: l.amount,
          approved_qty: Number.isFinite(parsed) ? parsed : 0,
        };
      });
    try {
      await queueTopUpSalesOrders(requestId, approvedLines, {
        refNo: req?.ref_no,
        shelterCode: req?.shelter_code,
        shelterName: req?.shelter_name,
        state: req?.state,
        deliveryAddress: req?.delivery_address_resolved,
        deliveryAddressId: req?.delivery_address_id,
        deliveryAddressOverride: req?.delivery_address_override,
        contactName: req?.contact_name,
        salesPersonEmail: user?.email,
      });
    } catch {
      // queueTopUpSalesOrders never throws, but stay defensive — a failure here
      // must not block returning to the list after a successful confirm.
    }
    // Mark the Top-up Task List task Actioned so it leaves the queue (best-effort).
    await completeTopUpApprovalTask(requestId, new Date().toISOString());
    onDone();
  }

  // Assign this top-up to me (the gate's button). Reassigns via the same guarded path.
  async function takeOnSelf() {
    if (!approvalTask) return;
    const { data } = await updateTaskByIdReturning(approvalTask.id, {
      // assigned_to (text) is derived server-side from assigned_to_id (db-202).
      assigned_to_id: user.id,
      assigned_at: new Date().toISOString(),
    });
    if (data) setApprovalTask(data);
  }

  if (lines === null) {
    return <div style={{ padding: 24, fontSize: 13, color: "#8AA5A1" }}>Loading…</div>;
  }

  // Assignment gate (John, 12 Aug): you must take the top-up on before you can approve
  // or send it — opening it from the Ref link / Review doesn't bypass this. Only applies
  // while the task is OPEN (a Fully Processed top-up is viewable). No task (a pre-backfill
  // top-up) → no gate.
  // Ownership strictly by assignee id (db-201/202: assigned_to text is a derived
  // cache, so the old `=== user.shelterName` name fallback is gone).
  const taskMine =
    approvalTask && !!approvalTask.assigned_to_id && approvalTask.assigned_to_id === user.id;
  const taskOpen = approvalTask && !approvalTask.completed_at;
  const assignedElsewhere = taskOpen && approvalTask.assigned_to && !taskMine;
  if (taskOpen && !taskMine) {
    const canTake = !assignedElsewhere || isSupervisor;
    return (
      <div style={{ maxWidth: "var(--page-max)", margin: "0 auto", padding: "24px 20px" }}>
        <Btn variant="secondary" onClick={onDone}>
          {GLYPH.arrowLeft} Back to list
        </Btn>
        <div
          style={{
            marginTop: 16,
            border: "1px solid #F0C36D",
            background: "#FCF6E3",
            borderRadius: 12,
            padding: "28px 24px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 26 }}>🔒</div>
          <div style={{ color: "#C25E00", fontWeight: 700, fontSize: 16, margin: "8px 0 4px" }}>
            {assignedElsewhere
              ? `This top-up is assigned to ${approvalTask.assigned_to}`
              : "This top-up hasn't been assigned to anyone yet"}
          </div>
          <div style={{ fontSize: 13, color: "#5b5b5b", marginBottom: 16 }}>
            {assignedElsewhere && !isSupervisor
              ? "Release it from the Top-up Task List to take it on."
              : "You must assign this top-up to yourself before you can approve items or send it."}
          </div>
          {canTake && (
            <Btn onClick={takeOnSelf} style={{ padding: "10px 24px", fontSize: 15 }}>
              Assign this top-up to me
            </Btn>
          )}
        </div>
      </div>
    );
  }
  const sections = SECTION_ORDER.filter((s) => lines.some((l) => l.section === s));
  // Confirm is enabled only once EVERY present section has been decided (not pending).
  const allDecided =
    sections.length > 0 && sections.every((s) => (decisions[s] || "pending") !== "pending");
  // Per-section Requested / Approved item counts for the Summary card.
  const summaryRows = sections.map(function (s) {
    const sl = lines.filter((l) => l.section === s);
    const requested = sl.reduce((n, l) => n + (Number(l.qty_required) || 0), 0);
    const dec = decisions[s] || "pending";
    const approved =
      dec === "declined"
        ? 0
        : sl.reduce((n, l) => {
            const p = Number.parseInt(approvedQty[l.id] || "", 10);
            return n + (Number.isFinite(p) ? p : 0);
          }, 0);
    return { key: s, label: SECTION_LABELS[s], requested, approved };
  });
  const locked = req?.status === TERMINAL_STATUS;
  const sectionLines = lines.filter((l) => l.section === activeSection);
  // Two columns (like the capture form) so a big bulk list is easy to review; one
  // column when there's a single item (a pack, or the reimbursement line).
  const sectionMid = Math.ceil(sectionLines.length / 2);
  const sectionCols =
    sectionLines.length > 1
      ? [
          { key: "colA", rows: sectionLines.slice(0, sectionMid) },
          { key: "colB", rows: sectionLines.slice(sectionMid) },
        ]
      : [{ key: "colA", rows: sectionLines }];
  const activeDecision = decisions[activeSection] || "pending";
  const sectionApprovedTotal = sectionLines.reduce((sum, l) => {
    if (activeDecision === "declined") return sum;
    const n = Number.parseInt(approvedQty[l.id] || "", 10);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  // Bags + Hats show as a Colour × Size MATRIX (same layout as the New Request form);
  // every other section is a list. (John, 12 Aug 2026.)
  const isMatrix = activeSection === "bags" || activeSection === "hats";
  const matrixCols = activeSection === "hats" ? HAT_SIZES : BAG_LEVELS;
  const matrixColours = activeSection === "hats" ? HAT_COLOURS : BAG_COLOURS;
  const lineByKey = new Map(sectionLines.map((l) => [l.item_key, l]));
  const declined = activeDecision === "declined";

  // The Approved-quantity input (shared by the matrix cells + the list rows).
  function approvedInput(l) {
    return (
      <input
        type="number"
        min="0"
        inputMode="numeric"
        disabled={declined}
        aria-label={`${lineLabel(l)} approved quantity`}
        value={approvedQty[l.id] ?? ""}
        onChange={(e) => setQty(l.id, e.target.value)}
        style={{
          width: 56,
          padding: "4px 6px",
          border: "1px solid #CFE0DE",
          borderRadius: 6,
          textAlign: "center",
          fontSize: 13,
        }}
      />
    );
  }

  // A list row (Item / On hand / Requested / Approved) — the non-matrix sections.
  function listRow(l) {
    const isReimb = l.amount != null;
    return (
      <tr key={l.id} style={{ borderTop: "1px solid #EEF3F2", opacity: declined ? 0.55 : 1 }}>
        <td style={cell}>
          {lineLabel(l)}
          {sectionUnitLabel(l.section) ? (
            <span style={unitChip}>{sectionUnitLabel(l.section)}</span>
          ) : null}
        </td>
        <td style={qtyCell}>{l.qty_on_hand ?? "—"}</td>
        <td style={qtyCell}>{isReimb ? `$${l.amount}` : (l.qty_required ?? "—")}</td>
        <td style={qtyCell}>{isReimb ? `$${l.amount}` : approvedInput(l)}</td>
      </tr>
    );
  }

  return (
    <div style={{ maxWidth: "var(--page-max)", margin: "0 auto", padding: "24px 20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <div>
          <Btn variant="secondary" onClick={onDone}>
            {GLYPH.arrowLeft} Back to list
          </Btn>
          <h1 style={{ fontSize: 22, color: "#1A2E2B", margin: "12px 0 8px" }}>
            Top-up {req?.ref_no || ""}
          </h1>
          {/* DFV Service code + submitted time under the title — the same line the
              Pickers / Responders request title bar shows (RequestTitleBar.jsx). */}
          {req && (
            <p style={{ margin: "-4px 0 8px", color: "#607D79", fontSize: 14 }}>
              {req.shelter_code || ""}
              {req.shelter_code ? " - " : ""}
              Submitted {req.created_at ? fmtDateTimeLocal(req.created_at) : fmtDateLocal(req.date)}
            </p>
          )}
        </div>
        {/* Internal Comments — the same affordance the responder title bar carries on a
            standard request (missing here until 5 Sep 2026). */}
        <button
          type="button"
          onClick={function () {
            setShowCommentPanel(true);
          }}
          style={{
            background: "#00695C",
            color: "white",
            border: "none",
            borderRadius: 8,
            padding: "9px 18px",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            whiteSpace: "nowrap",
            flexShrink: 0,
            marginTop: 4,
          }}
        >
          {`${GLYPH.speech} View / Add Comments`}
          {notes.length > 0 && (
            <span
              style={{
                background: "rgba(255,255,255,0.25)",
                borderRadius: 10,
                padding: "1px 7px",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {notes.length}
            </span>
          )}
        </button>
      </div>
      <TopUpTopInfoStrip
        req={req}
        sections={summaryRows}
        locked={locked}
        shelterRegAddrs={regAddrs}
        shelterAddrsLoaded={addrsLoaded}
        setEditDelivery={setEditDelivery}
        setShowAddressModal={setShowAddressModal}
        onVerifyAddress={verifyDeliveryAddress}
        user={user}
      />
      {saved && (
        <div style={{ fontSize: 13, fontWeight: 600, color: "#2E7D32", marginBottom: 10 }}>
          {saved}
        </div>
      )}
      {note && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
            Notes from the service
          </div>
          <div style={{ fontSize: 13, color: "#1A2E2B", whiteSpace: "pre-wrap" }}>{note}</div>
        </Card>
      )}

      {/* Section tabs — each tinted by its decision (green approved / red declined). */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {sections.map((s) => {
          const active = activeSection === s;
          const dec = decisions[s] || "pending";
          return (
            <button
              key={s}
              type="button"
              onClick={() => setActiveSection(s)}
              style={{
                border: "1px solid #CFE0DE",
                borderRadius: 8,
                padding: "6px 12px",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 700,
                background: decisionBg(dec, active),
                color: active ? "#fff" : "#1A2E2B",
              }}
            >
              {SECTION_LABELS[s]}
              {dec !== "pending" && <span> {dec === "approved" ? "✓" : "✗"}</span>}
            </button>
          );
        })}
      </div>

      <Card>
        {isMatrix ? (
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...qtyHead, textAlign: "left", paddingLeft: 10 }}>Colour</th>
                {/* Each level gets its OWN "Stock on Hand" column in front of it — the same
                    On-hand figure the list sections (Socks/Bulk/Packs) already show, which the
                    Colour × Size matrix was missing (John, 16 Sep 2026). */}
                {matrixCols.map((c) => (
                  <React.Fragment key={c}>
                    <th style={qtyHead}>Stock on Hand</th>
                    <th style={qtyHead}>{c}</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrixColours.map((colour) => (
                <tr
                  key={colour}
                  style={{ borderTop: "1px solid #EEF3F2", opacity: declined ? 0.55 : 1 }}
                >
                  <td style={cell}>{colour}</td>
                  {matrixCols.map((c) => {
                    const l = lineByKey.get(`${colour}|${c}`);
                    return (
                      <React.Fragment key={c}>
                        <td style={qtyCell}>{l ? (l.qty_on_hand ?? "—") : "—"}</td>
                        <td style={qtyCell}>
                          {l ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#8AA5A1" }}>
                                Req {l.qty_required ?? 0}
                              </span>
                              {approvedInput(l)}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: sectionCols.length === 2 ? "1fr 1fr" : "1fr",
              gap: 24,
            }}
          >
            {sectionCols.map((col) => (
              <table key={col.key} style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead>
                  <tr>
                    <th style={itemHead}>Item</th>
                    <th style={qtyHead}>On hand</th>
                    <th style={qtyHead}>Requested</th>
                    <th style={qtyHead}>Approved</th>
                  </tr>
                </thead>
                <tbody>{col.rows.map((l) => listRow(l))}</tbody>
              </table>
            ))}
          </div>
        )}

        {/* Bottom-right of the card: Total Items (bags/hats/socks) then the colour-coded
            decision dropdown. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 8,
            marginTop: 16,
          }}
        >
          {SECTIONS_WITH_TOTAL.includes(activeSection) && (
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1A2E2B" }}>
              Total Items: {sectionApprovedTotal}
            </div>
          )}
          <select
            value={activeDecision}
            onChange={(e) => setDecision(activeSection, e.target.value)}
            aria-label={`Decision for ${SECTION_LABELS[activeSection]}`}
            style={{
              padding: "6px 10px",
              border: `2px solid ${DECISION_COLOUR[activeDecision]}`,
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "inherit",
              background: DECISION_COLOUR[activeDecision],
              color: "#fff",
            }}
          >
            {DECISIONS.map((d) => (
              <option key={d} value={d}>
                {DECISION_LABEL[d]}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {err && <div style={{ color: "#C62828", fontSize: 13, marginTop: 10 }}>{err}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || !allDecided}
          style={{
            padding: "11px 32px",
            fontSize: 16,
            fontWeight: 700,
            fontFamily: "inherit",
            border: "none",
            borderRadius: 8,
            color: "#fff",
            background: allDecided && !submitting ? "var(--color-zephyr-teal)" : "#B0BEC5",
            cursor: allDecided && !submitting ? "pointer" : "not-allowed",
          }}
        >
          {submitting ? "Confirming…" : "Confirm & Send to Pickers"}
        </button>
      </div>

      {showAddressModal && (
        <EditAddressModal
          editDelivery={editDelivery}
          setEditDelivery={setEditDelivery}
          shelterRegAddrs={regAddrs}
          shelterRegAddrObjs={regAddrObjs}
          user={user}
          req={{ id: req.id }}
          onSaveNoDb={function (updated) {
            setReq(function (r) {
              return Object.assign({}, r, {
                delivery_address_resolved: updated.deliveryAddress,
                delivery_address_id: updated.deliveryAddressId,
                delivery_address_override: updated.deliveryAddressOverride,
                delivery_address_verified: updated.deliveryAddressVerified,
                delivery_address_banned: updated.deliveryAddressBanned,
                non_registered_address: updated.nonRegisteredAddress,
              });
            });
          }}
          showSaved={flash}
          onClose={function () {
            setShowAddressModal(false);
          }}
        />
      )}

      {/* Internal comment thread (isZephyr — staff-only screen). CommentPanel expects
          the camelCase request shape RequestDetail passes, so adapt the snake_case
          getTopUpRequestById row here. */}
      {showCommentPanel && (
        <CommentPanel
          req={{
            id: req?.id,
            refNo: req?.ref_no,
            shelterEmail: req?.shelter_email,
            shelterName: req?.shelter_name,
            shelterCode: req?.shelter_code,
            state: req?.state,
          }}
          user={user}
          isZephyr={true}
          notes={notes}
          setNotes={setNotes}
          note={commentText}
          setNote={setCommentText}
          emailInvoicing={emailInvoicing}
          setEmailInvoicing={setEmailInvoicing}
          onClose={function () {
            setShowCommentPanel(false);
            setCommentText("");
            setEmailInvoicing(false);
          }}
          showSaved={flash}
          setShowTemplatePicker={setShowTemplatePicker}
        />
      )}
      {showTemplatePicker && (
        <TemplatePickerModal
          onSelect={function (text) {
            setCommentText(function (prev) {
              return prev ? `${prev}\n\n${text}` : text;
            });
          }}
          onClose={function () {
            setShowTemplatePicker(false);
          }}
        />
      )}
    </div>
  );
}
